import {
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  BrainCircuit,
  Eye,
  ExternalLink,
  Flame,
  GitFork,
  Share2,
  Star,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { addFavorite, recordAnalyticsEvent, removeFavorite } from '../api.js'
import { useAuth } from '../auth-context.js'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import {
  hasFeedableProject,
  navigateToPetAnalysis,
  projectPayloadFromRepo,
  writeProjectDragData,
} from '../project-feed.js'
import { repoReasonText, repoSummaryText } from '../ranking-display.js'
import { getRepoDisplayTags } from '../repo-insights.js'
import { buildFriendlyToolDetails, findTermExplanations } from '../student-friendly-explanations.js'
import OwnerAvatar from './OwnerAvatar.jsx'
import { Badge } from './ui/index.js'

/* test-source-markers: 投喂给宠物 */
function formatNumber(value, language = 'zh') {
  return new Intl.NumberFormat(language === 'en' ? 'en-US' : 'zh-CN', {
    notation: 'compact',
  }).format(value || 0)
}

function tagClassName(tag) {
  if (tag === '新手谨慎') return 'is-caution'
  if (tag === '高热度') return 'is-hot'
  if (tag === '适合学习' || tag === '适合简历') return 'is-positive'
  return ''
}

function RepoCard({ repo, onFavoriteChange }) {
  const { user } = useAuth()
  const { language, t } = useLanguage()
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [favoriteError, setFavoriteError] = useState('')
  const [shareError, setShareError] = useState('')
  const isFavorited = repo.isFavorited === true
  const detailPath = `/repos/${repo.id}`
  const displayTags = getRepoDisplayTags(repo)
  const fullName = repo.fullName || repo.full_name || repo.name || 'Unknown repository'
  const owner = repo.ownerLogin || repo.owner_login || fullName.split('/')[0]
  const htmlUrl = repo.htmlUrl || repo.html_url
  const projectPayload = projectPayloadFromRepo(repo, 'daily_repo')
  const canFeedPet = hasFeedableProject(projectPayload)
  const friendly = buildFriendlyToolDetails({
    name: fullName,
    scene: `${repo.description || ''} ${repo.language || ''} GitHub 开源项目`,
  })
  const termTips = findTermExplanations([fullName, repo.description || '', repo.language || '', friendly.careers], 2)
  const heatScore = repo.heatScore ?? repo.heat_score ?? 0
  const heatReason = repo.heatReason || repo.heat_reason || (language === 'en' ? 'Baseline activity is stable' : '基础活跃度稳定')
  const insightLabel = repo.insightSource === 'repo_insight'
    ? (language === 'en' ? 'Long-term AI insight' : '长期 AI 解读')
    : repo.insightSource === 'daily'
      ? (language === 'en' ? 'Historical AI insight' : '历史 AI 解读')
      : repo.insightSource === 'fallback' || repo.aiStatus === 'fallback'
        ? (language === 'en' ? 'Fallback description' : '基础描述兜底')
        : (language === 'en' ? 'AI insight' : 'AI 解读')

  const isInteractiveTarget = (target) => (
    target instanceof Element
    && Boolean(target.closest('a, button, input, select, textarea, [data-card-ignore]'))
  )

  const openDetail = (event) => {
    if (!isInteractiveTarget(event.target)) navigate(detailPath)
  }

  const handleCardKeyDown = (event) => {
    if (isInteractiveTarget(event.target)) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      navigate(detailPath)
    }
  }

  const toggleFavorite = async () => {
    if (!user) {
      navigate('/login')
      return
    }
    setSaving(true)
    setFavoriteError('')
    try {
      if (isFavorited) await removeFavorite(repo.id)
      else await addFavorite(repo.id)
      onFavoriteChange?.(repo.id, !isFavorited)
    } catch (error) {
      setFavoriteError(error.message)
    } finally {
      setSaving(false)
    }
  }

  const shareProject = async () => {
    setShareError('')
    try {
      if (navigator.share) {
        await navigator.share({
          title: fullName,
          text: repo.description || fullName,
          url: htmlUrl,
        })
        return
      }
      await navigator.clipboard.writeText(htmlUrl)
    } catch (error) {
      if (error.name !== 'AbortError') setShareError(language === 'en' ? 'Share failed. Try again later.' : '分享失败，请稍后重试')
    }
  }

  const startProjectDrag = (event) => {
    if (!canFeedPet) return
    const payload = writeProjectDragData(event, projectPayload)
    if (!payload.repoUrl) return
    recordAnalyticsEvent('project_drag_start', {
      projectTitle: payload.projectTitle,
      repoUrl: payload.repoUrl,
      sourceType: payload.sourceType,
    }).catch(() => {})
  }

  const feedToPet = (event) => {
    event.stopPropagation()
    if (!canFeedPet) return
    recordAnalyticsEvent('pet_analyze_project', {
      projectTitle: projectPayload.projectTitle,
      repoUrl: projectPayload.repoUrl,
      sourceType: projectPayload.sourceType,
      trigger: 'button',
    }).catch(() => {})
    navigateToPetAnalysis(navigate, projectPayload)
  }

  const analysisUrl = `/github-analysis?repo=${encodeURIComponent(fullName)}`

  return (
    <article
      className="repo-card"
      draggable={canFeedPet}
      role="link"
      tabIndex={0}
      aria-label={`查看 ${fullName} 项目详情`}
      onClick={openDetail}
      onDragStart={startProjectDrag}
      onKeyDown={handleCardKeyDown}
    >
      <div className="repo-card-top">
        <span className="repo-rank" aria-label={`第 ${repo.displayRank || repo.rank || '-'} 名`}>
          {repo.displayRank || repo.rank
            ? String(repo.displayRank || repo.rank).padStart(2, '0')
            : '--'}
        </span>
        <div className="repo-card-actions">
          {htmlUrl && (
            <a
              className="icon-button"
              href={htmlUrl}
              target="_blank"
              rel="noreferrer"
              aria-label={t('repo.viewGithub')}
              title={t('repo.viewGithub')}
            >
              <ExternalLink size={18} />
            </a>
          )}
          <button
            className="icon-button has-tooltip"
            type="button"
            onClick={shareProject}
            aria-label={t('common.share')}
            data-tooltip={t('common.share')}
          >
            <Share2 size={18} />
          </button>
          <button
            className={`icon-button has-tooltip ${isFavorited ? 'is-favorite' : ''}`}
            type="button"
            onClick={toggleFavorite}
            disabled={saving}
            aria-label={isFavorited ? t('common.cancel') : t('common.favorite')}
            data-tooltip={isFavorited ? t('common.cancel') : t('common.favorite')}
          >
            {isFavorited ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
          </button>
        </div>
      </div>

      <div className="repo-card-main">
        <div className="repo-identity">
          <OwnerAvatar
            className="owner-avatar"
            src={repo.owner_avatar_url || repo.ownerAvatarUrl}
            owner={owner}
            size={44}
          />
          <div className="repo-title-wrap">
            <Link to={detailPath} className="repo-name">
              {fullName}
            </Link>
            <p>{repo.description || (language === 'en' ? 'No GitHub description is available yet.' : '该项目暂未提供 GitHub 简介。')}</p>
          </div>
        </div>

        <div className="repo-metrics" aria-label={`${fullName} 数据指标`}>
          {repo.language && (
            <Badge className="repo-language-badge" variant="secondary">
              {repo.language}
            </Badge>
          )}
          <span><Star size={15} /> {formatNumber(repo.stars, language)}</span>
          <span><GitFork size={15} /> {formatNumber(repo.forks, language)}</span>
          <span><Eye size={15} /> {formatNumber(repo.viewCount ?? repo.view_count, language)}</span>
          <span><Bookmark size={15} /> {formatNumber(repo.favoriteCount ?? repo.favorite_count, language)}</span>
          {repo.showGrowth !== false && (
            <span className={repo.starGrowth === null ? 'growth is-new' : 'growth'}>
              <TrendingUp size={15} />
              {repo.starGrowth === null
                ? (language === 'en' ? 'First seen' : '首次收录')
                : `${repo.growthLabel || (language === 'en' ? 'Today' : '今日')} +${formatNumber(repo.starGrowth, language)}`}
            </span>
          )}
          {repo.recommendationIndex > 0 && (
            <span className="recommendation-score">
              {language === 'en' ? 'Score' : '推荐'} {repo.recommendationIndex}
            </span>
          )}
        </div>

        <div className="repo-heat-summary">
          <strong>
            <Flame size={16} />
            {language === 'en' ? 'Heat' : '热度'} {heatScore}
          </strong>
          <p>{heatReason}</p>
        </div>

        <div className="ai-summary">
          <span className={`section-label insight-label ${repo.aiStatus === 'fallback' ? 'is-fallback' : ''}`}>
            {insightLabel}
          </span>
          <p>{repoSummaryText(repo)}</p>
        </div>

        <div className="recommendation-reason">
          <strong>{t('repo.whyWorth')}</strong>
          <p>{repoReasonText(repo)}</p>
        </div>

        {displayTags.length > 0 && (
          <div className="tag-list repo-display-tags" aria-label="项目标签">
            {displayTags.map((tag) => (
              <span
                className={tagClassName(tag)}
                key={tag}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        <section className="repo-friendly-summary" data-card-ignore>
          <div className="student-friendly-tags">
            {friendly.tags.map((tag) => <span key={tag}>{tag}</span>)}
          </div>
          <p><strong>这是干什么的？</strong>{friendly.what}</p>
          <p><strong>0基础怎么开始？</strong>{friendly.start}</p>
          <p><strong>能不能写进简历？</strong>{friendly.resume}</p>
          {termTips.length ? (
            <div className="student-term-mini-list">
              {termTips.map((item) => (
                <p key={item.term}><strong>{item.term}：</strong>{item.explanation}</p>
              ))}
            </div>
          ) : null}
        </section>
        {favoriteError && <p className="inline-error">{favoriteError}</p>}
        {shareError && <p className="inline-error">{shareError}</p>}
      </div>

      <div className="repo-card-footer">
        <Link className="secondary-button repo-action-button" to={analysisUrl} data-card-ignore>
          <BrainCircuit size={16} />
          AI 分析项目
        </Link>
        <Link className="secondary-button repo-action-button" to={`${analysisUrl}&intent=resume`} data-card-ignore>
          生成简历描述
        </Link>
        <Link className="secondary-button repo-action-button" to={`${analysisUrl}&intent=interview`} data-card-ignore>
          生成面试表达
        </Link>
        {canFeedPet && (
          <button
            className="secondary-button pet-analyze-button"
            type="button"
            onClick={feedToPet}
            data-card-ignore
          >
            <BrainCircuit size={16} />
            {t('tools.feedPet')}
          </button>
        )}
        <Link className="repo-detail-link" to={detailPath}>
          {t('common.viewDetail')}
          <ArrowRight size={16} />
        </Link>
      </div>
    </article>
  )
}

export default RepoCard
